import type { BlokProps } from "@trydig/storyblok";
import { classed, clsx } from "@trydig/ui";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { ArticleCard } from "@/components/ui/ArticleCard";
import { Breadcrumbs, BreadcrumbsSkeleton } from "@/components/ui/Breadcrumbs";
import { HideBreadcrumbsGate } from "@/components/ui/Breadcrumbs/visibility";
import { Container } from "@/components/ui/Container";
import { Skeleton } from "@/components/ui/Skeleton";
import { Typography } from "@/components/ui/Typography";
import type { ArticlesRoute } from "@/data/routes/articles.route";
import {
	ArticleList,
	ArticleListSkeleton,
} from "@/features/articles/ArticleListingPage/components/ArticleList";
import {
	TagFilter,
	TagFilterSkeleton,
} from "@/features/articles/ArticleListingPage/components/TagFilter";
import type { ArticleListingPageStoryblok } from "@/generated/sb/sb-types";
import { capitalizeFirstLetter } from "@/lib/utils/capitalizeFirstLetter";

export const ArticleListingPageBlok = ({
	blok,
	slug,
	data,
}: BlokProps<ArticleListingPageStoryblok, ArticlesRoute["list"]> & {
	slug?: string[];
}) => {
	if (!data) return null;

	return (
		<BlokWrap editable={blok} asChild>
			<Main>
				<Header>
					{slug && (
						<HideBreadcrumbsGate>
							<Breadcrumbs
								items={slug.map((label, i) => {
									const isLast = i === slug.length - 1;
									const href = isLast ? undefined : `/${slug.slice(0, i + 1).join("/")}`;

									return { label: capitalizeFirstLetter(label), href };
								})}
							/>
						</HideBreadcrumbsGate>
					)}
					<Typography
						variant="heading-display-md"
						className="mt-grid-section-vertical-md mb-grid-section-vertical-lg"
					>
						{blok.header}
					</Typography>
					<TagFilter tags={data.allTags} />
				</Header>
				<ArticleList
					storySlug={data.storySlug}
					initialArticles={data.initialArticles}
					total={data.total}
					content={data.featuredArticles.map((article, i) => {
						const isEven = i % 2 === 0;
						const mobileRowStart = i * 2 + 2;
						const mobileRowEnd = i * 2 + 3;
						const desktopRowStart = i * 2 + 2;
						const desktopRowEnd = i * 2 + 3;

						return (
							<div
								key={i}
								className={clsx(
									"featured-content col-span-2 h-full",
									isEven ? "lg:col-start-2 lg:col-end-4" : "lg:col-start-1 lg:col-end-3",
								)}
								style={
									{
										"--mobile-row-start": mobileRowStart,
										"--mobile-row-end": mobileRowEnd,
										"--desktop-row-start": desktopRowStart,
										"--desktop-row-end": desktopRowEnd,
									} as React.CSSProperties
								}
							>
								<ArticleCard {...article} variant="horizontal-highlight" className="h-full" />
							</div>
						);
					})}
				/>
			</Main>
		</BlokWrap>
	);
};

export const ArticleListingPageSkeleton = ({ blok }: BlokProps<ArticleListingPageStoryblok>) => {
	return (
		<BlokWrap editable={blok} asChild>
			<Main>
				<Header>
					<HideBreadcrumbsGate>
						<BreadcrumbsSkeleton />
					</HideBreadcrumbsGate>
					<Skeleton fitContent>
						<Typography
							variant="heading-display-md"
							className="mt-grid-section-vertical-md mb-grid-section-vertical-lg"
						>
							{blok.header}
						</Typography>
					</Skeleton>
					<TagFilterSkeleton />
				</Header>
				<ArticleListSkeleton />
			</Main>
		</BlokWrap>
	);
};

const Main = classed.main("bg-brand-sand-10");

const Header = classed(
	Container,
	"py-grid-section-vertical-md mb-grid-section-vertical-md flex flex-col border-b border-neutral-black-30 bg-background-bg-sand-1",
);
